import { useMemo } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import type { TranslatableLabel } from '#src/app/shared/hooks/i18n';
import { useCopyLinkConfirmation } from '#src/app/shared/hooks/share';

const RESET_LABEL_TIMER = 3000;

type ShareData = {
	clickedState: boolean;
	buttonLabel: TranslatableLabel;
	resolvedUrl: string;
	/** See `useCopyLinkConfirmation`. */
	copyAnnouncementKey: number | null;
};
type ShareActions = Pick<ReturnType<typeof useCopyLinkConfirmation>, 'press' | 'afterShare'>;
type UseDefaultShareButtonResult = ModelHookResult<ShareData, ShareActions>;
type UseCopyLinkVisibleUrlButton = {
	label: TranslatableLabel;
	successLabel?: TranslatableLabel;
	url: string;
	/** The URL the share action copies, when it differs from `url`. */
	shortUrl?: string;
};
export function useCopyLinkVisibleUrlButton({
	label,
	successLabel,
	url,
	shortUrl,
}: UseCopyLinkVisibleUrlButton): UseDefaultShareButtonResult {
	const { copyAnnouncementKey, press, afterShare } = useCopyLinkConfirmation({
		copyUrl: shortUrl ?? url,
		resetMs: RESET_LABEL_TIMER,
	});

	const clickedState = !!successLabel && copyAnnouncementKey !== null;

	const buttonLabel = useMemo(
		() => (successLabel && clickedState ? successLabel : label),
		[clickedState, successLabel, label],
	);

	const { resolveUrl } = useUtilityContext();
	const resolvedUrl = useMemo(() => {
		return resolveUrl(url);
	}, [url, resolveUrl]);

	return {
		data: { clickedState, buttonLabel, resolvedUrl, copyAnnouncementKey },
		actions: { press, afterShare },
	};
}
